import { useQuery } from "@tanstack/react-query";
import { useTenant } from "@/context/tenant-context";
import { useWorkspace } from "@/context/workspace-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { issuesApi } from "@/api/issues";
import { queryKeys } from "@/lib/query-keys";
import { ListChecks, Bot, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

const statusColors: Record<string, string> = {
  backlog: "bg-gray-400",
  todo: "bg-blue-400",
  in_progress: "bg-amber-400",
  done: "bg-green-400",
  cancelled: "bg-red-400",
};

export function CoworkSurface() {
  const { tenantId } = useTenant();
  const { threadContext, updateThreadContext } = useWorkspace();
  const { data: agents = [] } = useAgentsByCompany(tenantId);

  const { data: issues = [] } = useQuery({
    queryKey: queryKeys.issues.byCompany(tenantId),
    queryFn: () => issuesApi.list(tenantId),
    enabled: !!tenantId,
  });

  const selectedIssueId = threadContext.issueId ?? null;
  const selectedIssue = issues.find((i) => i.id === selectedIssueId);

  // Group issues by status
  const columns = ["todo", "in_progress", "done"] as const;
  const grouped = columns.map((status) => ({
    status,
    label: status === "in_progress" ? "In Progress" : status.charAt(0).toUpperCase() + status.slice(1),
    items: issues.filter((i) => i.status === status),
  }));

  return (
    <div className="flex h-full">
      {/* Kanban board */}
      <div className="flex flex-1 gap-4 overflow-x-auto p-4">
        {grouped.map((col) => (
          <div key={col.status} className="flex w-[280px] shrink-0 flex-col">
            <div className="mb-2 flex items-center gap-2 px-1">
              <div className={cn("h-2 w-2 rounded-full", statusColors[col.status])} />
              <span className="text-xs font-semibold text-text-secondary uppercase">{col.label}</span>
              <span className="ml-auto text-xs text-text-muted">{col.items.length}</span>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto">
              {col.items.map((issue) => {
                const assignedAgent = agents.find((a) => a.id === issue.assigneeAgentId);
                return (
                  <button
                    key={issue.id}
                    onClick={() => updateThreadContext({ issueId: issue.id })}
                    className={cn(
                      "w-full rounded-lg border p-3 text-left transition-colors",
                      issue.id === selectedIssueId
                        ? "border-accent bg-accent/5"
                        : "border-border bg-surface hover:border-accent/30",
                    )}
                  >
                    <p className="text-xs font-medium text-text-primary line-clamp-2">{issue.title}</p>
                    {issue.identifier && (
                      <p className="mt-1 text-[10px] text-text-muted">{issue.identifier}</p>
                    )}
                    {assignedAgent && (
                      <div className="mt-2 flex items-center gap-1 text-[10px] text-text-muted">
                        <Bot size={10} />
                        <span>{assignedAgent.name}</span>
                      </div>
                    )}
                  </button>
                );
              })}
              {col.items.length === 0 && (
                <div className="rounded-lg border border-dashed border-border p-4 text-center">
                  <p className="text-xs text-text-muted">No items</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Detail panel */}
      {selectedIssue ? (
        <div className="w-[320px] shrink-0 border-l border-border bg-sidebar overflow-y-auto">
          <div className="border-b border-border px-4 py-3">
            <p className="text-xs text-text-muted">{selectedIssue.identifier}</p>
            <h3 className="mt-1 text-sm font-semibold text-text-primary">{selectedIssue.title}</h3>
          </div>
          <div className="px-4 py-3 space-y-3">
            <div>
              <p className="text-[10px] font-medium text-text-muted uppercase mb-1">Status</p>
              <div className="flex items-center gap-1.5">
                <div className={cn("h-2 w-2 rounded-full", statusColors[selectedIssue.status])} />
                <span className="text-xs text-text-secondary">{selectedIssue.status}</span>
              </div>
            </div>
            <div>
              <p className="text-[10px] font-medium text-text-muted uppercase mb-1">Priority</p>
              <span className="text-xs text-text-secondary">{selectedIssue.priority}</span>
            </div>
            {selectedIssue.assigneeAgentId && (
              <div>
                <p className="text-[10px] font-medium text-text-muted uppercase mb-1">Assigned Agent</p>
                <div className="flex items-center gap-1.5">
                  <Bot size={12} className="text-accent" />
                  <span className="text-xs text-text-secondary">
                    {agents.find((a) => a.id === selectedIssue.assigneeAgentId)?.name ?? "Unknown"}
                  </span>
                </div>
              </div>
            )}
            {selectedIssue.description && (
              <div>
                <p className="text-[10px] font-medium text-text-muted uppercase mb-1">Description</p>
                <p className="text-xs text-text-secondary whitespace-pre-wrap">{selectedIssue.description}</p>
              </div>
            )}
            {/* Quick action: jump to chat with assigned agent */}
            {selectedIssue.assigneeAgentId && (
              <button
                onClick={() => {
                  updateThreadContext({ agentId: selectedIssue.assigneeAgentId! });
                  // Switch to chat mode - the workspace context handles this
                }}
                className="flex w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-xs text-text-secondary hover:border-accent hover:text-accent transition-colors"
              >
                <ArrowRight size={12} />
                Chat with agent
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="w-[320px] shrink-0 border-l border-border bg-sidebar flex items-center justify-center">
          <div className="text-center">
            <ListChecks size={32} className="mx-auto mb-2 text-text-muted" />
            <p className="text-xs text-text-muted">Select a task to view details</p>
          </div>
        </div>
      )}
    </div>
  );
}
